/**
 * This reducer handles all state changes for the content cache.
 *
 */ import type {
    Action,
    CourseByIdState,
    LearnableContentByIdState,
    LessonByIdState,
    SlugToIdMapState,
    State,
    UnitByIdState,
    UnlistedUnitIdsByCourseIdState,
} from "./content-cache-types.ts";

/**
 * coursesByIdReducer is responsible for all updates to the coursesById
 * subtree of the ContentCache state.
 */
const coursesByIdReducer = (
    state: CourseByIdState,
    action: Action,
): CourseByIdState => {
    switch (action.type) {
        case "SET_COURSE":
        case "SET_COURSE_AND_CONTENT": {
            const {course} = action.data;
            return course ? {...state, [course.id]: course} : state;
        }

        default:
            return state;
    }
};

/**
 * courseIdBySlugReducer is responsible for all updates to the courseIdBySlug
 * subtree of the ContentCache state.
 */
const courseIdBySlugReducer = (
    state: SlugToIdMapState,
    action: Action,
): SlugToIdMapState => {
    switch (action.type) {
        case "SET_COURSE":
        case "SET_COURSE_AND_CONTENT": {
            const {course} = action.data;
            return course?.slug ? {...state, [course.slug]: course.id} : state;
        }

        default:
            return state;
    }
};

/**
 * unitsByIdReducer is responsible for all updates to the unitsById
 * subtree of the ContentCache state.
 */
const unitsByIdReducer = (
    state: UnitByIdState,
    action: Action,
): UnitByIdState => {
    switch (action.type) {
        case "SET_COURSE":
        case "SET_COURSE_AND_CONTENT": {
            const {course, unit} = action.data;
            const newState = course.unitChildren.reduce(
                (unitsById, unit) => {
                    if (unit) {
                        unitsById[unit.id] = unit;
                    }
                    return unitsById;
                },
                {...state},
            );

            // We request the explicit Unit when making the CourseQuery
            // in the case of an unlisted path.
            if (unit) {
                newState[unit.id] = unit;
            }

            return newState || state;
        }

        default:
            return state;
    }
};

/**
 * unitIdBySlugReducer is responsible for all updates to the unitIdBySlug
 * subtree of the ContentCache state.
 */
const unitIdBySlugReducer = (
    state: SlugToIdMapState,
    action: Action,
): SlugToIdMapState => {
    switch (action.type) {
        case "SET_COURSE":
        case "SET_COURSE_AND_CONTENT": {
            const {course, unit} = action.data;
            const newState = course.unitChildren.reduce(
                (unitIdBySlug, unit) => {
                    unitIdBySlug[unit.slug] = unit.id;
                    return unitIdBySlug;
                },
                {...state},
            );

            // We request the explicit Unit when making the CourseQuery
            // in the case of an unlisted path.
            if (unit) {
                newState[unit.slug] = unit.id;
            }

            return newState || state;
        }

        default:
            return state;
    }
};

/**
 * lessonsByIdReducer is responsible for all updates to the unitsById
 * subtree of the ContentCache state.
 */
const lessonsByIdReducer = (
    state: LessonByIdState,
    action: Action,
): LessonByIdState => {
    switch (action.type) {
        case "SET_COURSE_AND_CONTENT": {
            const {lesson} = action.data;
            return lesson ? {...state, [lesson.id]: lesson} : state;
        }

        default:
            return state;
    }
};

/**
 * lessonsIdBySlugReducer is responsible for all updates to the unitIdBySlug
 * subtree of the ContentCache state.
 */
const lessonsIdBySlugReducer = (
    state: SlugToIdMapState,
    action: Action,
): SlugToIdMapState => {
    switch (action.type) {
        case "SET_COURSE_AND_CONTENT": {
            const {lesson} = action.data;
            return lesson?.slug ? {...state, [lesson.slug]: lesson.id} : state;
        }

        default:
            return state;
    }
};

/**
 * learnableContentByIdReducer is responsible for all updates to the
 * learnableContentById subtree of the ContentCache state.
 */
const learnableContentByIdReducer = (
    state: LearnableContentByIdState,
    action: Action,
): LearnableContentByIdState => {
    switch (action.type) {
        case "SET_COURSE_AND_CONTENT": {
            const {content} = action.data;
            return content ? {...state, [content.id]: content} : state;
        }

        case "SET_LEARNABLE_CONTENT": {
            const {content} = action.data;
            return content ? {...state, [content.id]: content} : state;
        }

        default:
            return state;
    }
};

/**
 * learnableContentIdByKindAndSlugReducer is responsible for all updates to the
 * learnableContentIdByKindAndSlug subtree of the ContentCache state.
 */
const learnableContentIdByKindAndSlugReducer = (
    state: SlugToIdMapState,
    action: Action,
): SlugToIdMapState => {
    switch (action.type) {
        case "SET_COURSE_AND_CONTENT": {
            const {content} = action.data;
            return content.slug
                ? {
                      ...state,
                      [`${content.contentKind}:${content.slug}`]: content.id,
                  }
                : state;
        }

        case "SET_LEARNABLE_CONTENT": {
            const {content} = action.data;
            return content.slug
                ? {
                      ...state,
                      [`${content.contentKind}:${content.slug}`]: content.id,
                  }
                : state;
        }

        default:
            return state;
    }
};

function unlistedUnitIdsByCourseIdReducer(
    state: UnlistedUnitIdsByCourseIdState,
    action: Action,
): UnlistedUnitIdsByCourseIdState {
    switch (action.type) {
        case "SET_COURSE_AND_CONTENT":
        case "SET_COURSE": {
            const {course, unlistedUnitIds} = action.data;
            return {
                ...state,
                [course.id]: unlistedUnitIds,
            };
        }

        default:
            return state;
    }
}

export const initialState: State = {
    courseById: {},
    courseIdBySlug: {},
    unitById: {},
    unitIdBySlug: {},
    lessonById: {},
    lessonIdBySlug: {},
    learnableContentById: {},
    learnableContentIdByKindAndSlug: {},
    unlistedUnitIdsByCourseId: {},
};

const reducer = (state: State = initialState, action: Action): State => {
    switch (action.type) {
        case "CLEAR_CONTENT_CACHE": {
            return initialState;
        }

        case "SET_COURSE": {
            return {
                ...state,
                publishedContentVersion: action.publishedContentVersion,
                courseById: coursesByIdReducer(state.courseById, action),
                courseIdBySlug: courseIdBySlugReducer(
                    state.courseIdBySlug,
                    action,
                ),
                unitById: unitsByIdReducer(state.unitById, action),
                unitIdBySlug: unitIdBySlugReducer(state.unitIdBySlug, action),
                unlistedUnitIdsByCourseId: unlistedUnitIdsByCourseIdReducer(
                    state.unlistedUnitIdsByCourseId,
                    action,
                ),
            };
        }

        case "SET_COURSE_AND_CONTENT": {
            const {course, content} = action.data;
            if (!course && !content) {
                return state;
            }

            return {
                ...state,
                publishedContentVersion: action.publishedContentVersion,
                courseById: coursesByIdReducer(state.courseById, action),
                courseIdBySlug: courseIdBySlugReducer(
                    state.courseIdBySlug,
                    action,
                ),
                unitById: unitsByIdReducer(state.unitById, action),
                unitIdBySlug: unitIdBySlugReducer(state.unitIdBySlug, action),
                learnableContentById: learnableContentByIdReducer(
                    state.learnableContentById,
                    action,
                ),
                learnableContentIdByKindAndSlug:
                    learnableContentIdByKindAndSlugReducer(
                        state.learnableContentIdByKindAndSlug,
                        action,
                    ),
                unlistedUnitIdsByCourseId: unlistedUnitIdsByCourseIdReducer(
                    state.unlistedUnitIdsByCourseId,
                    action,
                ),
                lessonById: lessonsByIdReducer(state.lessonById, action),
                lessonIdBySlug: lessonsIdBySlugReducer(
                    state.lessonIdBySlug,
                    action,
                ),
            };
        }

        case "SET_LEARNABLE_CONTENT": {
            return {
                ...state,
                publishedContentVersion: action.publishedContentVersion,
                learnableContentById: learnableContentByIdReducer(
                    state.learnableContentById,
                    action,
                ),
                learnableContentIdByKindAndSlug:
                    learnableContentIdByKindAndSlugReducer(
                        state.learnableContentIdByKindAndSlug,
                        action,
                    ),
            };
        }
    }
};

export default reducer;
